import React from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { X, Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import LazyImage from "./LazyImage";

// Estilos CSS personalizados para el scrollbar - agregar al inicio del archivo
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(55, 65, 81, 0.3);
    border-radius: 10px;
    margin: 4px 0;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(156, 163, 175, 0.4);
    border-radius: 10px;
    transition: all 0.3s ease;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(156, 163, 175, 0.6);
  }
  
  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(156, 163, 175, 0.4) rgba(55, 65, 81, 0.3);
  }
`;

// Agregar al inicio del componente para inyectar los estilos
const ScrollbarStyles = () => {
  return <style>{scrollbarStyles}</style>;
};

const QuoteModal = ({
  isOpen,
  onClose,
  product,
  selectedColor = 0,
  phoneNumbers = [
    { number: "3338217408", display: "(33) 3821 7408" },
    { number: "3318715068", display: "(33) 1871 5068" },
  ],
  getWhatsAppUrl,
}) => {
  const { t } = useTranslation();

  if (!product) return null;

  return (
    <>
      <ScrollbarStyles />
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay - Corregido zIndex para que aparezca por encima de header */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-80 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm"
              onClick={onClose}
            >
              {/* Modal Content - RESPONSIVE */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="bg-gray-900 rounded-xl shadow-2xl w-full mx-auto my-4 max-h-[90vh] overflow-y-auto border border-red-900 flex flex-col md:flex-row custom-scrollbar"
                style={{
                  boxShadow: `0 25px 50px -12px rgba(220, 38, 38, 0.35)`,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Columna izquierda: Información del producto - RESPONSIVE */}
                <div className="w-full md:w-5/12 p-4 md:p-6 border-b md:border-b-0 md:border-r border-red-900/30">
                  <div className="flex justify-between items-start mb-3 md:mb-4">
                    <h3 className="text-xl md:text-2xl font-bold text-white">
                      {t("quoteModal.title")}
                    </h3>
                    {/* Botón de cerrar visible solo en móviles */}
                    <button
                      onClick={onClose}
                      className="text-gray-400 hover:text-white transition-colors md:hidden"
                      aria-label="Cerrar"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  <div className="flex flex-col items-center">
                    {/* Imagen del producto - Mejorada para móvil */}
                    <div className="w-36 h-36 md:w-48 md:h-48 bg-gray-800 rounded-xl overflow-hidden border border-red-900/20 mb-3 md:mb-6 flex items-center justify-center mx-auto">
                      <LazyImage
                        src={
                          product.mainImage ||
                          product.gallery?.[0] ||
                          product.imagen
                        }
                        alt={product.name || product.producto}
                        className="w-full h-full object-contain p-2 md:p-4"
                      />
                    </div>

                    <div className="text-center w-full px-2">
                      <h4 className="font-bold text-lg md:text-xl text-white mb-1 md:mb-2">
                        {product.name || product.producto}
                      </h4>

                      <p className="text-sm md:text-base text-gray-300 mb-1">
                        {t("quoteModal.material")}{" "}
                        {product.specifications?.find(
                          (spec) => spec.name === "Composición"
                        )?.value || product.material}
                      </p>

                      {product.colors && (
                        <div className="flex items-center justify-center mt-1 md:mt-2 mb-2 md:mb-4">
                          <span className="text-xs md:text-sm font-medium text-gray-300 mr-1 md:mr-2">
                            {t("quoteModal.color")}
                          </span>
                          <div className="flex items-center space-x-1 md:space-x-2">
                            <span
                              className="w-5 h-5 md:w-6 md:h-6 rounded-full border-2 border-red-500/50"
                              style={{
                                backgroundColor:
                                  product.colors[selectedColor]?.hex ||
                                  product.hexadecimal ||
                                  product.color?.toLowerCase() ||
                                  "#333",
                              }}
                            />
                            <span className="text-xs md:text-sm text-gray-200">
                              {product.colors?.[selectedColor]?.name ||
                                product.color ||
                                t("quoteModal.notSpecified")}
                            </span>
                          </div>
                        </div>
                      )}

                      <p className="text-xs md:text-sm text-gray-400 mt-1 md:mt-2 px-1 md:px-4">
                        {product.description || product.descripcion}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Columna derecha: Botones de contacto - RESPONSIVE */}
                <div className="w-full md:w-7/12 p-4 md:p-6 flex flex-col justify-center">
                  {/* Botón de cerrar visible solo en desktop */}
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition-colors absolute top-3 right-3 md:top-4 md:right-4 hidden md:block"
                    aria-label="Cerrar"
                  >
                    <X size={24} />
                  </button>

                  <div className="space-y-3 md:space-y-4">
                    <a
                      href={getWhatsAppUrl(product.name || product.producto)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="button-animate flex items-center justify-center gap-2 md:gap-3 py-3 md:py-4 px-4 md:px-6 text-xs md:text-sm font-medium rounded-lg md:rounded-xl bg-green-900/30 text-green-300 border border-green-700/30 transition-all duration-300 hover:bg-green-900 hover:border-green-600 hover:shadow-lg hover:shadow-green-900/50"
                    >
                      <img
                        src="/images/Whatsapp_icon.svg"
                        alt="WhatsApp"
                        className="h-5 w-5 md:h-6 md:w-6"
                      />
                      <span className="font-semibold text-xs md:text-sm">
                        {t("quoteModal.contactWhatsapp")}
                      </span>
                    </a>

                    {phoneNumbers.map((phone, index) => (
                      <a
                        key={index}
                        href={`tel:${phone.number}`}
                        className="button-animate flex items-center justify-center gap-2 md:gap-3 py-3 md:py-4 px-4 md:px-6 text-xs md:text-sm font-medium rounded-lg md:rounded-xl bg-blue-900/30 text-blue-300 border border-blue-700/30 transition-all duration-300 hover:bg-blue-900 hover:border-blue-600 hover:shadow-lg hover:shadow-blue-900/50"
                      >
                        <Phone className="h-4 w-4 md:h-5 md:w-5" />
                        <span className="font-semibold text-xs md:text-sm">
                          {t("quoteModal.call")} {phone.display}
                        </span>
                      </a>
                    ))}

                    <Link
                      to="/contacto"
                      onClick={onClose}
                      className="button-animate flex items-center justify-center gap-2 md:gap-3 py-3 md:py-4 px-4 md:px-6 text-xs md:text-sm font-medium rounded-lg md:rounded-xl bg-red-900/30 text-red-300 border border-red-700/30 transition-all duration-300 hover:bg-red-900 hover:border-red-600 hover:shadow-lg hover:shadow-red-900/50"
                    >
                      <Mail className="h-4 w-4 md:h-5 md:w-5" />
                      <span className="font-semibold text-xs md:text-sm">
                        {t("quoteModal.sendEmail")}
                      </span>
                    </Link>
                  </div>

                  <p className="text-[0.65rem] md:text-xs text-center mt-4 md:mt-8 text-gray-500 px-2">
                    {t("quoteModal.footerNote")}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default QuoteModal;
